import {
  FGListen,
  FGSelectInput,
  FGTextInput,
  FieldGroup,
  useApiEffect,
} from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import * as Yup from "yup";

import { ClinicApi, ClinicDto, CountryApi } from "../../../api";
import { ERoutes } from "../../../ERoutes";
import {
  FGCountrySelect,
  SharepointButton,
  SmallFormGenerator,
} from "../../../components";
import { useApiService, useCrudApi, useTl } from "../../../hooks";
import { useReferential } from "../../../hooks/useReferential";
import { ETLCodes } from "../../../locales";

export interface IClinicDetailsPageProps {
  clinicId: number;
  editMode: boolean;
}

export const ClinicDetailsPage: React.FunctionComponent<IClinicDetailsPageProps> = ({
  clinicId,
  editMode,
}) => {
  const api = useApiService(ClinicApi);
  const countryApi = useApiService(CountryApi);
  const { t } = useTl();
  const history = useHistory();

  const [postalCode, setPostalCode] = React.useState("");
  const [isBelgium, setIsBelgium] = React.useState<boolean>(false);

  const [languages, lloading] = useReferential(
    (a) => a.getLanguages(),
    true,
    []
  );
  const [postalCodes, pcloading] = useReferential(
    (a) => a.getStringifiedBelgianPostalCodes(),
    true,
    []
  );
  const [localities, locloading] = useReferential(
    (a) => a.getStringifiedBelgianLocalityFromPostalCode(postalCode ?? ""),
    false,
    [postalCode]
  );
  const [baseCountryId] = useApiEffect(
    async () => await countryApi.getBelgianId(),
    []
  );

  const {
    data,
    loading,
    saveItem,
    saving,
    deleteItem,
    deleting,
  } = useCrudApi<ClinicDto>(
    {
      getApiFn: (id = clinicId) =>
        id > 0 ? api.get(id) : new ClinicDto().toJSON(),
      saveApiFn: (c: ClinicDto) => api.save(c),
      onSavedRoute: (c) => `${ERoutes.clinic}/${c.clinicId}/detail/edit`,
      deleteApiFn: (c) => api.delete(c.clinicId),
      onDeletedRoute: (d) => `${ERoutes.clinic}`,
    },
    [clinicId]
  );
  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      name: Yup.string().nullable().required(t(ETLCodes.Required)),
      languageId: Yup.number().nullable().required(t(ETLCodes.Required)),
      siteweb: Yup.string().nullable(),
      tva: Yup.string().nullable(),
      email: Yup.string()
        .nullable()
        .email(t(ETLCodes.Email))
        .required(t(ETLCodes.Required)),
      street: Yup.string().nullable().required(t(ETLCodes.Required)),
      number: Yup.string().nullable().required(t(ETLCodes.Required)),
      postalCode: Yup.string().nullable().required(t(ETLCodes.Required)),
      countryId: Yup.number().nullable().required(t(ETLCodes.Required)),
      city: Yup.string().nullable().required(t(ETLCodes.Required)),
      phoneNumber: Yup.string().nullable(),
    });
  }, [t]);

  return (
    <>
      <SmallFormGenerator
        initialValues={data}
        onSubmit={saveItem}
        editMode={editMode}
        loading={loading}
        onCancel={() => history.push(ERoutes.clinic)}
        saving={saving}
        deleting={deleting}
        showDeleteButton={clinicId > 0}
        onDelete={deleteItem}
        validationSchema={FormSchema}
      >
        <FGListen
          field="countryId"
          onChanged={(value, formik) => {
            if (!value && clinicId === 0) {
              formik.setFieldValue("countryId", baseCountryId);
              setIsBelgium(true);
            } else {
              if (value === baseCountryId) {
                setIsBelgium(true);
              } else {
                setIsBelgium(false);
              }
            }
          }}
        />
        <FGListen
          field="postalCode"
          onChanged={(value) => {
            if (isBelgium) {
              setPostalCode(value);
            }
          }}
        />
        <FieldGroup
          columns={2}
          fieldsetProps={{
            rightElement: clinicId > 0 && (
              <SharepointButton onClick={() => {}} />
            ),
            title: t(ETLCodes.Clinics),
          }}
        >
          <FGTextInput name="name" label={t(ETLCodes.Name)} />
          <FGTextInput name="street" label={t(ETLCodes.Street)} />

          <FGTextInput name="email" label={t(ETLCodes.Email)} />
          <FGTextInput name="number" label={t(ETLCodes.StreetNumber)} />

          <FGTextInput
            name="phoneNumber"
            label={t(ETLCodes.PhoneNumber)}
            requiredMark
          />
          {isBelgium ? (
            <FGSelectInput
              name="postalCode"
              items={postalCodes}
              loading={pcloading}
              label={t(ETLCodes.PostalCode)}
            />
          ) : (
            <FGTextInput name="postalCode" label={t(ETLCodes.PostalCode)} />
          )}

          {isBelgium ? (
            <FGSelectInput
              name="city"
              items={localities}
              loading={locloading}
              label={t(ETLCodes.City)}
            />
          ) : (
            <FGTextInput name="city" label={t(ETLCodes.City)} />
          )}

          <FGTextInput name="tva" label={t(ETLCodes.VATNumber)} />
          <FGCountrySelect name="countryId" label={t(ETLCodes.Country)} />

          <FGSelectInput
            name="languageId"
            items={languages}
            loading={lloading}
            label={t(ETLCodes.Language)}
            requiredMark
          />
        </FieldGroup>
      </SmallFormGenerator>
    </>
  );
};
